import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft, MessageCircle, Send, Search, User,
    ChevronRight, Check, CheckCheck, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MessagesPage() {
    const navigate = useNavigate();
    const { conversationId } = useParams();
    const { token, user } = useAuth();
    const [view, setView] = useState(conversationId ? 'chat' : 'list'); // 'list' | 'chat' | 'new'
    const [loading, setLoading] = useState(true);
    
    // Data
    const [conversations, setConversations] = useState([]);
    const [residents, setResidents] = useState([]);
    const [activeConversation, setActiveConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    
    // New message
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);

    const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const scrollToBottom = useCallback(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, []);

    const fetchConversations = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/messages/conversations`, { headers: headers() });
            setConversations(res.data);
        } catch (error) {
            console.error('Error fetching conversations:', error);
        } finally {
            setLoading(false);
        }
    }, [headers]);

    const fetchResidents = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/residents/directory`, { headers: headers() });
            setResidents(res.data);
        } catch (error) {
            console.error('Error fetching residents:', error);
        }
    }, [headers]);

    const fetchConversation = useCallback(async (convId) => {
        try {
            setLoading(true);
            const [convRes, msgRes] = await Promise.all([
                axios.get(`${API}/messages/conversations/${convId}`, { headers: headers() }),
                axios.get(`${API}/messages/conversations/${convId}/messages`, { headers: headers() })
            ]);
            setActiveConversation(convRes.data);
            setMessages(msgRes.data);
            setView('chat');
            
            // Mark as read
            await axios.put(`${API}/messages/conversations/${convId}/read`, {}, { headers: headers() });
            fetchConversations();
        } catch (error) {
            console.error('Error fetching conversation:', error);
            toast.error('Failed to load conversation');
        } finally {
            setLoading(false);
        }
    }, [headers, fetchConversations]);

    useEffect(() => {
        fetchConversations();
        fetchResidents();
    }, [fetchConversations, fetchResidents]);

    useEffect(() => {
        if (conversationId) {
            fetchConversation(conversationId);
        }
    }, [conversationId, fetchConversation]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, scrollToBottom]);

    const startConversation = async (resident) => {
        try {
            const res = await axios.post(`${API}/messages/conversations`, 
                { recipient_id: resident.id }, 
                { headers: headers() }
            );
            setActiveConversation(res.data);
            setMessages([]);
            setView('chat');
            navigate(`/messages/${res.data.id}`);
        } catch (error) {
            toast.error('Could not start conversation');
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !activeConversation) return;

        setSending(true);
        try {
            const res = await axios.post(
                `${API}/messages/conversations/${activeConversation.id}`,
                { content: newMessage.trim() },
                { headers: headers() }
            );
            setMessages([...messages, res.data]);
            setNewMessage('');
            fetchConversations(); // Refresh list to update preview
        } catch (error) {
            toast.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diff = now - date;
        
        // Today: show time
        if (diff < 24 * 60 * 60 * 1000 && date.getDate() === now.getDate()) {
            return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
        }
        // This week: show day
        if (diff < 7 * 24 * 60 * 60 * 1000) {
            return date.toLocaleDateString('en-GB', { weekday: 'short' });
        }
        // Older: show date
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };

    const filteredResidents = residents.filter(r => 
        r.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getInitials = (name) => {
        return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
    };

    return (
        <div className="min-h-screen bg-stone-50 flex flex-col" data-testid="messages-page">
            {/* Header */}
            <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white px-6 pt-6 pb-8">
                <button 
                    onClick={() => view === 'chat' ? setView('list') : navigate('/home')}
                    className="flex items-center gap-2 text-indigo-200 hover:text-white mb-4"
                    data-testid="back-btn"
                >
                    <ArrowLeft className="w-5 h-5" />
                    {view === 'chat' ? 'Messages' : 'Home'}
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <MessageCircle className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-serif text-white">
                            {view === 'chat' && activeConversation?.other_participant 
                                ? activeConversation.other_participant.name 
                                : view === 'new' ? 'New Message' : 'Messages'}
                        </h1>
                        <p className="text-white/80 text-sm">
                            {view === 'new' ? 'Select a resident' : 'Stay connected with neighbors'}
                        </p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col">
                {loading ? (
                    <div className="flex-1 flex justify-center items-center">
                        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* Conversations List */}
                        {view === 'list' && (
                            <div className="flex-1 pb-24">
                                {/* New Message Button */}
                                <div className="p-4">
                                    <Button
                                        onClick={() => setView('new')}
                                        className="w-full bg-indigo-600 hover:bg-indigo-700"
                                        data-testid="new-message-btn"
                                    >
                                        <MessageCircle className="w-4 h-4 mr-2" />
                                        New Message
                                    </Button>
                                </div>

                                {conversations.length === 0 ? (
                                    <div className="px-4 py-12 text-center">
                                        <MessageCircle className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                                        <h3 className="font-semibold text-stone-700 mb-2">No Messages Yet</h3>
                                        <p className="text-stone-500 text-sm">
                                            Start a conversation with a fellow resident
                                        </p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-stone-100">
                                        {conversations.map((conv) => (
                                            <button
                                                key={conv.id}
                                                onClick={() => {
                                                    navigate(`/messages/${conv.id}`);
                                                    fetchConversation(conv.id);
                                                }}
                                                className="w-full px-4 py-4 flex items-center gap-4 hover:bg-stone-100 transition-colors text-left"
                                                data-testid={`conversation-${conv.id}`}
                                            >
                                                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                                                    <span className="text-indigo-600 font-semibold">
                                                        {getInitials(conv.other_participant?.name)}
                                                    </span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center justify-between">
                                                        <h3 className="font-semibold text-stone-800 truncate">
                                                            {conv.other_participant?.name || 'Unknown'}
                                                        </h3>
                                                        <span className="text-xs text-stone-400 flex-shrink-0 ml-2">
                                                            {formatTime(conv.last_message_at)}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-stone-500 truncate mt-0.5">
                                                        {conv.last_message_preview || 'No messages yet'}
                                                    </p>
                                                </div>
                                                {conv.unread_count > 0 && (
                                                    <div className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs flex items-center justify-center flex-shrink-0">
                                                        {conv.unread_count}
                                                    </div>
                                                )}
                                                <ChevronRight className="w-5 h-5 text-stone-400 flex-shrink-0" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* New Message - Resident Selection */}
                        {view === 'new' && (
                            <div className="flex-1 pb-24">
                                <div className="p-4">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                        <Input
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            placeholder="Search residents..."
                                            className="pl-10"
                                            data-testid="search-residents"
                                        />
                                    </div>
                                </div>

                                <div className="px-4 mb-2">
                                    <p className="text-xs text-stone-500 uppercase tracking-wider">
                                        Residents ({filteredResidents.length})
                                    </p>
                                </div>

                                <div className="divide-y divide-stone-100">
                                    {filteredResidents.map((resident) => (
                                        <button
                                            key={resident.id}
                                            onClick={() => startConversation(resident)}
                                            className="w-full px-4 py-3 flex items-center gap-4 hover:bg-stone-100 transition-colors text-left"
                                            data-testid={`resident-${resident.id}`}
                                        >
                                            <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center">
                                                <span className="text-stone-600 font-medium text-sm">
                                                    {getInitials(resident.name)}
                                                </span>
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-medium text-stone-800">{resident.name}</h3>
                                                {resident.apartment && (
                                                    <p className="text-xs text-stone-500">Apt {resident.apartment}</p>
                                                )}
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-stone-400" />
                                        </button>
                                    ))}
                                </div>

                                {filteredResidents.length === 0 && (
                                    <div className="px-4 py-8 text-center text-stone-500">
                                        No residents found
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Chat View */}
                        {view === 'chat' && activeConversation && (
                            <div className="flex-1 flex flex-col">
                                {/* Messages */}
                                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                    {messages.length === 0 ? (
                                        <div className="text-center py-8">
                                            <p className="text-stone-500 text-sm">
                                                Start the conversation with a friendly message!
                                            </p>
                                        </div>
                                    ) : (
                                        messages.map((msg) => {
                                            const isMine = msg.sender_id === user?.id;
                                            return (
                                                <div
                                                    key={msg.id}
                                                    className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                                                    data-testid={`message-${msg.id}`}
                                                >
                                                    <div
                                                        className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                                                            isMine
                                                                ? 'bg-indigo-600 text-white rounded-br-md'
                                                                : 'bg-white border border-stone-200 text-stone-800 rounded-bl-md'
                                                        }`}
                                                    >
                                                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                                        <div className={`flex items-center gap-1 mt-1 ${isMine ? 'justify-end' : ''}`}>
                                                            <span className={`text-xs ${isMine ? 'text-indigo-200' : 'text-stone-400'}`}>
                                                                {formatTime(msg.created_at)}
                                                            </span>
                                                            {isMine && (
                                                                msg.read_by?.length > 1 
                                                                    ? <CheckCheck className="w-3.5 h-3.5 text-indigo-200" />
                                                                    : <Check className="w-3.5 h-3.5 text-indigo-200" />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Message Input */}
                                <form 
                                    onSubmit={handleSendMessage}
                                    className="p-4 bg-white border-t border-stone-200 flex items-center gap-3"
                                >
                                    <Input
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        placeholder="Type a message..."
                                        className="flex-1"
                                        disabled={sending}
                                        data-testid="message-input"
                                    />
                                    <Button
                                        type="submit"
                                        disabled={!newMessage.trim() || sending}
                                        className="bg-indigo-600 hover:bg-indigo-700 px-4"
                                        data-testid="send-message-btn"
                                    >
                                        <Send className="w-4 h-4" />
                                    </Button>
                                </form>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default MessagesPage;
